import { Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { Star, Truck, ShieldCheck, Plus, Check, Heart } from "lucide-react";
import { rupee, optimg } from "@/lib/api";
import { useCompare } from "@/context/CompareContext";
import { useWishlist } from "@/context/WishlistContext";

export default function ProductCard({ product, index = 0 }) {
  const { ids, toggle } = useCompare();
  const { has, toggle: toggleWish } = useWishlist();
  const [loaded, setLoaded] = useState(false);
  const inCompare = ids.includes(product.id);
  const saved = has(product.id);
  const price = product.discount ? Math.round(product.price * (1 - product.discount / 100)) : product.price;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.06, ease: [0.22, 1, 0.36, 1] }}
      className="group"
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative aspect-[4/5] overflow-hidden rounded-sm bg-surface">
        <div className={`absolute inset-0 bg-gradient-to-br from-surface to-line/40 transition-opacity duration-500 ${loaded ? "opacity-0" : "opacity-100 animate-pulse"}`} />
        <Link to={`/product/${product.id}`}>
          <img
            src={optimg(product.images?.[0], 600, 68)}
            alt={product.title}
            loading="lazy"
            decoding="async"
            onLoad={() => setLoaded(true)}
            className={`w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-105 ${loaded ? "opacity-100 blur-0" : "opacity-0 blur-md scale-105"}`}
          />
        </Link>
        {product.discount > 0 && (
          <span className="absolute top-3 left-3 bg-clay text-sand text-[11px] px-2.5 py-1 rounded-full">-{product.discount}%</span>
        )}
        <button
          onClick={() => toggleWish(product.id)}
          className={`absolute top-3 right-3 w-8 h-8 grid place-items-center rounded-full transition-all ${
            saved ? "bg-clay text-sand" : "bg-sand/90 text-ink hover:bg-clay hover:text-sand"
          }`}
          data-testid={`wishlist-toggle-${product.id}`}
          aria-label="Save to favourites"
        >
          <Heart className={`w-4 h-4 ${saved ? "fill-sand" : ""}`} />
        </button>
        <button
          onClick={() => toggle(product.id)}
          className={`absolute top-12 right-3 w-8 h-8 grid place-items-center rounded-full transition-all ${
            inCompare ? "bg-forest text-sand" : "bg-sand/90 text-ink hover:bg-forest hover:text-sand"
          }`}
          data-testid={`compare-toggle-${product.id}`}
          aria-label="Add to compare"
        >
          {inCompare ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </button>
      </div>

      <div className="pt-3.5">
        <div className="flex items-center justify-between gap-2">
          <Link to={`/seller/${product.seller_id}`} className="overline text-mute hover:text-clay truncate">{product.seller_name}</Link>
          {product.review_count > 0 && (
            <span className="flex items-center gap-1 text-xs text-mute shrink-0">
              <Star className="w-3.5 h-3.5 fill-terra text-terra" /> {product.rating}
            </span>
          )}
        </div>
        <Link to={`/product/${product.id}`}>
          <h3 className="font-serif text-lg leading-snug mt-1 tracking-tight group-hover:text-clay transition-colors">{product.title}</h3>
        </Link>
        <div className="flex items-center gap-2 mt-1.5">
          <span className="text-base font-semibold">{rupee(price)}</span>
          {product.discount > 0 && <span className="text-sm text-mute line-through">{rupee(product.price)}</span>}
        </div>
        <div className="flex items-center gap-3 mt-2 text-[11px] text-mute">
          <span className="flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> {product.delivery_days}d</span>
          <span className="flex items-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5" /> {product.refundable ? "Refundable" : "Non-refundable"}
          </span>
        </div>
      </div>
    </motion.div>
  );
}
